import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../Services/api';
import { useAuth } from '../../Contexts/AuthContext';
import styles from '../../styles/social.module.css';

const PIPELINE_STATUSES = [
  { id: 'New', name: 'New' },
  { id: 'Contacted', name: 'Contacted' },
  { id: 'Interested', name: 'Interested' },
  { id: 'Form Filled', name: 'Form Filled' },
  { id: 'Admission Done', name: 'Admission Done' },
  { id: 'Pipeline', name: 'Pipeline' },
  { id: 'Lost', name: 'Lost' },
  { id: 'Course Not Available', name: 'Course Not Available' },
  { id: 'Will Think On It', name: 'Will Think On It' },
  { id: 'Missed', name: 'Missed' }
];

export default function MetaAdsDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const [pageError, setPageError] = useState('');
  const [pagesList, setPagesList] = useState([]);
  const [adAccountsList, setAdAccountsList] = useState([]);
  const [selectedDatePreset, setSelectedDatePreset] = useState('last_7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Mapping Form state
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [campaignName, setCampaignName] = useState('');

  // Buffer state
  const [bufferLeads, setBufferLeads] = useState([]);
  const [bufferLoading, setBufferLoading] = useState(false);
  const [transferModalLead, setTransferModalLead] = useState(null);
  const [transferMode, setTransferMode] = useState('merge'); // 'create' or 'merge'
  const [duplicateWarning, setDuplicateWarning] = useState('');

  useEffect(() => {
    // Detect URL parameter for OAuth callback connection status
    const params = new URLSearchParams(window.location.search);
    if (params.get('meta_ads_connected') === 'true') {
      toast.success('🎉 Meta Ads account linked successfully!', {
        duration: 5000,
        style: {
          border: '1px solid #799F0C',
          padding: '16px',
          color: '#3d5c04',
        },
        iconTheme: {
          primary: '#799F0C',
          secondary: '#fff',
        },
      });
      // Scrub query parameters from browser URL bar without page reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  useEffect(() => {
    fetchConnectedAccount();
  }, []);

  useEffect(() => {
    if (account && activeTab === 'buffer') {
      fetchBufferLeads();
    }
  }, [activeTab, account]);

  const fetchConnectedAccount = async () => {
    try {
      setLoading(true);
      const res = await api.get('meta-ads/accounts/');
      if (res.data && res.data.length > 0) {
        const activeAcc = res.data[0];
        setAccount(activeAcc);
        fetchCampaigns(activeAcc.id);
        fetchMappings();
        fetchForms(activeAcc.id);
        fetchPages(activeAcc.id);
        fetchAdAccounts(activeAcc.id);
      } else {
        setAccount(null);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to retrieve Meta account info');
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async (accountId) => {
    try {
      const res = await api.get(`meta-ads/accounts/${accountId}/pages/`);
      setPagesList(res.data || []);
    } catch (err) {
      console.error('Failed to retrieve Facebook pages list:', err);
    }
  };

  const fetchAdAccounts = async (accountId) => {
    try {
      const res = await api.get(`meta-ads/accounts/${accountId}/ad-accounts/`);
      setAdAccountsList(res.data || []);
    } catch (err) {
      console.error('Failed to retrieve Facebook ad accounts list:', err);
    }
  };

  const fetchCampaigns = async (accountId, preset = selectedDatePreset, start = startDate, end = endDate) => {
    try {
      let url = `meta-ads/accounts/${accountId}/campaigns/`;
      if (preset === 'custom') {
        if (start && end) {
          url += `?start_date=${start}&end_date=${end}`;
        }
      } else {
        url += `?date_preset=${preset}`;
      }
      const res = await api.get(url);
      setCampaigns(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to retrieve campaigns list');
    }
  };

  const fetchForms = async (accountId) => {
    setPageError('');
    try {
      const res = await api.get(`meta-ads/accounts/${accountId}/lead-forms/`);
      setLeadForms(res.data || []);
    } catch (err) {
      console.error('Lead forms list fetch error:', err);
      const errMsg = err.response?.data?.error || err.message || 'Failed to retrieve lead forms';
      setPageError(errMsg);
    }
  };

  const fetchMappings = async () => {
    try {
      const res = await api.get('meta-ads/mappings/');
      setMappings(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBufferLeads = async () => {
    try {
      setBufferLoading(true);
      const res = await api.get('meta-ads/buffer/');
      setBufferLeads(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to retrieve buffer leads');
    } finally {
      setBufferLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await api.get('meta-ads/oauth-url/');
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error('Failed to get OAuth url');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Meta Ads account? All synchronization services will be disabled.')) return;
    try {
      await api.delete(`meta-ads/accounts/${account.id}/`);
      toast.success('Disconnected successfully');
      setAccount(null);
      setCampaigns([]);
      setMappings([]);
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const handleToggleCampaign = async (campaignId, currentStatus) => {
    const nextStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await api.post(`meta-ads/accounts/${account.id}/toggle-campaign/`, {
        campaign_id: campaignId,
        status: nextStatus
      });
      toast.success(`Campaign updated to ${nextStatus}`);
      fetchCampaigns(account.id);
    } catch (err) {
      toast.error('Failed to toggle campaign status');
    }
  };

  const handleCreateMapping = async (e) => {
    e.preventDefault();
    if (!selectedForm || !selectedStatus) {
      toast.error('Please select a form and target status');
      return;
    }
    const formObj = leadForms.find(f => f.id === selectedForm);
    try {
      await api.post('meta-ads/mappings/', {
        ad_account: account.id,
        form_id: selectedForm,
        form_name: formObj ? formObj.name : 'Unknown Form',
        campaign_name: campaignName,
        default_status: selectedStatus
      });
      toast.success('Mapping rule configured successfully');
      fetchMappings();
      setSelectedForm('');
      setSelectedStatus('');
      setCampaignName('');
    } catch (err) {
      toast.error('Failed to create mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Delete this mapping rule? Direct leads generated by this form will no longer sync to CRM.')) return;
    try {
      await api.delete(`meta-ads/mappings/${mappingId}/`);
      toast.success('Mapping rule removed');
      fetchMappings();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete mapping rule');
    }
  };

  const handleTransferLead = async (lead, force = false, mode = 'create') => {
    try {
      const res = await api.post(`meta-ads/buffer/${lead.id}/transfer/`, { force, mode });
      if (res.data.duplicate_found) {
        setTransferModalLead(lead);
        setDuplicateWarning(res.data.message);
        setTransferMode('merge');
      } else {
        toast.success(res.data.message || 'Lead transferred successfully!');
        setTransferModalLead(null);
        setDuplicateWarning('');
        fetchBufferLeads();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to transfer lead');
    }
  };

  const handleIgnoreLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to ignore this lead? It will be archived.')) return;
    try {
      await api.post(`meta-ads/buffer/${leadId}/ignore/`);
      toast.success('Lead archived and ignored');
      fetchBufferLeads();
    } catch (err) {
      console.error(err);
      toast.error('Failed to ignore lead');
    }
  };

  if (loading) {
    return (
      <div className={styles['sm-root']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '32px', animation: 'spin 1.5s linear infinite' }}>♻️</div>
        <div style={{ color: '#9ca3af', fontWeight: '600', fontSize: '14px' }}>Loading Meta Integration Dashboard...</div>
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}} />
      </div>
    );
  }

  // Analytics Calculations
  const totalSpend = campaigns.reduce((sum, c) => sum + parseFloat(c.spend || 0), 0);
  const totalImpressions = campaigns.reduce((sum, c) => sum + intVal(c.impressions), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + intVal(c.clicks), 0);
  const totalLeads = campaigns.reduce((sum, c) => sum + intVal(c.leads), 0);
  const totalReach = campaigns.reduce((sum, c) => sum + intVal(c.reach), 0);

  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  const avgCpm = totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;
  const avgCpl = totalLeads > 0 ? totalSpend / totalLeads : 0;

  const overallConversion = totalImpressions > 0 ? (totalLeads / totalImpressions) * 100 : 0;

  const topCampaigns = [...campaigns]
    .sort((a, b) => parseFloat(b.spend || 0) - parseFloat(a.spend || 0))
    .slice(0, 5);
  const maxSpend = topCampaigns.length > 0 ? parseFloat(topCampaigns[0].spend || 1) : 1;

  const objectiveGroup = campaigns.reduce((acc, c) => {
    const obj = (c.objective || 'OTHER').replace(/_/g, ' ').toUpperCase();
    if (!acc[obj]) {
      acc[obj] = { spend: 0, leads: 0, count: 0 };
    }
    acc[obj].spend += parseFloat(c.spend || 0);
    acc[obj].leads += intVal(c.leads);
    acc[obj].count += 1;
    return acc;
  }, {});

  const objectivesList = Object.entries(objectiveGroup).map(([name, data]) => ({
    name,
    spend: data.spend,
    leads: data.leads,
    count: data.count,
  })).sort((a, b) => b.spend - a.spend);

  const maxObjectiveSpend = objectivesList.length > 0 ? objectivesList[0].spend : 1;

  const getStatusCfg = (statusText) => {
    const norm = (statusText || '').toLowerCase();
    if (norm === 'active') {
      return { bg: "#edf7ee", color: "#2a7a38", dot: "#36a849", label: "Active" };
    }
    return { bg: "#f3f4f6", color: "#6b7280", dot: "#9ca3af", label: "Paused" };
  };

  return (
    <div className={styles['sm-root']}>
      {/* HEADER SECTION */}
      <div className={styles['sm-header']}>
        <div className={styles['sm-title-block']}>
          <div className={styles['sm-eyebrow']}>Meta Ads Integration</div>
          <div className={styles['sm-title']}>Marketing & Lead Center</div>
          <div className={styles['sm-subtitle']}>Synchronize active campaigns, monitor real-time metrics, and manage Webhook lead forms mappings.</div>
        </div>
        
        <div className={styles['sm-header-btns']}>
          {!account ? (
            <button 
              className={styles['sm-add-btn']} 
              onClick={handleConnect}
              style={{ background: 'linear-gradient(135deg, #1877F2, #115cbf)', boxShadow: '0 4px 10px rgba(24, 119, 242, 0.28)' }}
            >
              <i className="fa-brands fa-facebook" style={{ fontSize: '15px' }} /> Connect Meta Ads Account
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div className={styles['sm-client-chip']} style={{ cursor: 'default', background: '#eef2ff', borderColor: '#c7d2fe' }}>
                <div className={styles['sm-client-av']} style={{ background: '#1877F2', color: '#fff' }}>
                  <i className="fa-brands fa-facebook-f" />
                </div>
                <span className={styles['sm-client-name']} style={{ color: '#3730a3' }}>
                  {account.page_id === 'all' ? 'All Pages' : (pagesList.find(p => p.id === account.page_id)?.name || account.business_name || 'Business Page')}
                </span>
              </div>
              <button 
                className={styles['sm-io-btn']} 
                style={{ color: '#b91c1c', borderColor: '#fca5a5', background: '#fef2f2' }} 
                onClick={handleDisconnect}
              >
                <i className="fa-solid fa-power-off" /> Disconnect
              </button>
            </div>
          )}
        </div>
      </div>

      {/* FILTER CONTROL BAR */}
      {account && (
        <div className={styles['sm-table-card']} style={{ padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', flex: 1 }}>
            {pagesList.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 'bold', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <i className="fa-solid fa-file-lines" style={{ color: '#799F0C' }} /> Active Page:
                </span>
                <select
                  value={account.page_id || ''}
                  onChange={async (e) => {
                    const newPageId = e.target.value;
                    if (!newPageId) return;
                    try {
                      const patchRes = await api.patch(`meta-ads/accounts/${account.id}/`, { page_id: newPageId });
                      const pageName = newPageId === 'all' ? 'All Pages' : (pagesList.find(p => p.id === newPageId)?.name || 'Updated');
                      toast.success(`Connected to page: ${pageName}`);
                      setAccount(patchRes.data);
                      fetchForms(account.id);
                      fetchCampaigns(account.id);
                    } catch (err) {
                      toast.error('Failed to switch Facebook Page');
                    }
                  }}
                  className={styles['sm-form-input']}
                  style={{ margin: 0, padding: '8px 12px', fontSize: '12.5px', background: '#fff', width: '200px', display: 'inline-block' }}
                >
                  <option value="">-- Choose Page --</option>
                  <option value="all">All Pages</option>
                  {pagesList.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
            {adAccountsList.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 'bold', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <i className="fa-solid fa-wallet" style={{ color: '#799F0C' }} /> Ad Account:
                </span>
                <select
                  value={account.ad_account_id || ''}
                  onChange={async (e) => {
                    const newAdAccountId = e.target.value;
                    if (!newAdAccountId) return;
                    const selectedAcc = adAccountsList.find(acc => acc.id === newAdAccountId);
                    try {
                      const patchRes = await api.patch(`meta-ads/accounts/${account.id}/`, { 
                         ad_account_id: newAdAccountId,
                         business_name: newAdAccountId === 'all' ? 'All Ad Accounts' : (selectedAcc ? (selectedAcc.business_name || selectedAcc.name) : account.business_name),
                         currency: selectedAcc ? selectedAcc.currency : account.currency
                      });
                      const accName = newAdAccountId === 'all' ? 'All Ad Accounts' : (selectedAcc ? selectedAcc.name : 'Updated');
                      toast.success(`Connected to Ad Account: ${accName}`);
                      setAccount(patchRes.data);
                      fetchCampaigns(account.id);
                    } catch (err) {
                      toast.error('Failed to switch Ad Account');
                    }
                  }}
                  className={styles['sm-form-input']}
                  style={{ margin: 0, padding: '8px 12px', fontSize: '12.5px', background: '#fff', width: '220px', display: 'inline-block' }}
                >
                  <option value="">-- Choose Ad Account --</option>
                  <option value="all">All Ad Accounts</option>
                  {adAccountsList.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12.5px', fontWeight: 'bold', color: '#4b5563', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <i className="fa-solid fa-calendar-days" style={{ color: '#799F0C' }} /> Date Range:
              </span>
              <select
                value={selectedDatePreset}
                onChange={async (e) => {
                  const newPreset = e.target.value;
                  setSelectedDatePreset(newPreset);
                  if (newPreset !== 'custom') {
                    setStartDate('');
                    setEndDate('');
                    fetchCampaigns(account.id, newPreset, '', '');
                  }
                }}
                className={styles['sm-form-input']}
                style={{ margin: 0, padding: '8px 12px', fontSize: '12.5px', background: '#fff', width: '160px', display: 'inline-block' }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_3d">Last 3 Days</option>
                <option value="last_7d">Last 7 Days</option>
                <option value="last_30d">Last 30 Days</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="lifetime">Lifetime</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {selectedDatePreset === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f9fafb', padding: '8px 16px', borderRadius: '12px', border: '1.5px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#4b5563' }}>From:</span>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                  className={styles['sm-form-input']}
                  style={{ margin: 0, padding: '6px 12px', fontSize: '12.5px', background: '#fff', width: '150px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#4b5563' }}>To:</span>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                  className={styles['sm-form-input']}
                  style={{ margin: 0, padding: '6px 12px', fontSize: '12.5px', background: '#fff', width: '150px' }}
                />
              </div>
              <button 
                onClick={() => {
                  if (!startDate || !endDate) {
                    toast.error('Please select both start and end dates');
                    return;
                  }
                  fetchCampaigns(account.id, 'custom', startDate, endDate);
                }}
                className={styles['sm-add-btn']}
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
 
      {/* METADATA OR CONNECTED CONTAINER */}
      {account ? (
        <>
          {/* TABS SELECTION */}
          <div className={styles['sm-summary']}>
            <div 
              className={`${styles['sm-pill']} ${activeTab === 'overview' ? styles['active'] : ''}`} 
              onClick={() => setActiveTab('overview')}
            >
              <i className="fa-solid fa-chart-line" /> Performance Report
            </div>
            <div 
              className={`${styles['sm-pill']} ${activeTab === 'campaigns' ? styles['active'] : ''}`} 
              onClick={() => setActiveTab('campaigns')}
            >
              <i className="fa-solid fa-bullhorn" /> Campaigns List
            </div>
            <div 
              className={`${styles['sm-pill']} ${activeTab === 'mappings' ? styles['active'] : ''}`} 
              onClick={() => setActiveTab('mappings')}
            >
              <i className="fa-solid fa-link" /> Webhook Lead Mapping
            </div>
            <div 
              className={`${styles['sm-pill']} ${activeTab === 'buffer' ? styles['active'] : ''}`} 
              onClick={() => setActiveTab('buffer')}
            >
              <i className="fa-solid fa-inbox" /> Staging Leads Buffer
            </div>
          </div>
          
          {pageError && (
            <div className={styles['sm-cal-card']} style={{ margin: '0 0 24px 0', border: '1.5px solid #fca5a5', background: '#fef2f2', borderRadius: '12px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ fontSize: '22px', color: '#b91c1c' }}>⚠️</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: '13.5px', fontWeight: '800', color: '#991b1b' }}>Facebook Page Sync Blocked</h4>
                  <p style={{ margin: '0 0 14px 0', fontSize: '12.5px', color: '#b91c1c', lineHeight: '1.5' }}>
                    {pageError}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {pagesList.length > 0 ? (
                      <select 
                        className={styles['sm-form-input']}
                        style={{ margin: 0, padding: '8px 12px', background: '#fff', fontSize: '12.5px', flex: 1, minWidth: '220px', maxWidth: '380px' }}
                        id="manual-page-id-input"
                        defaultValue={account?.page_id || ''}
                      >
                        <option value="">-- Choose Facebook Page --</option>
                        {pagesList.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input 
                        type="text" 
                        placeholder="Enter Page ID manually (e.g. 1029384756)"
                        className={styles['sm-form-input']}
                        style={{ margin: 0, padding: '8px 12px', background: '#fff', fontSize: '12.5px', flex: 1, minWidth: '220px', maxWidth: '380px' }}
                        id="manual-page-id-input"
                        defaultValue={account?.page_id || ''}
                      />
                    )}
                    <button 
                      type="button"
                      onClick={async () => {
                        const inputVal = document.getElementById('manual-page-id-input')?.value?.trim();
                        if (!inputVal) {
                          toast.error('Please select or enter a valid Page ID');
                          return;
                        }
                        try {
                          const patchRes = await api.patch(`meta-ads/accounts/${account.id}/`, { page_id: inputVal });
                          toast.success('Facebook Page linked successfully!');
                          setAccount(patchRes.data);
                          fetchForms(account.id);
                        } catch (err) {
                          toast.error('Failed to link Facebook Page');
                        }
                      }}
                      className={styles['sm-add-btn']}
                      style={{ padding: '8px 16px', fontSize: '12.5px' }}
                    >
                      Link Page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* OVERVIEW / PERFORMANCE REPORT TAB */}
          {activeTab === 'overview' && (
            <div>
              <div className={styles['sm-analytics-grid']}>
                <div className={styles['sm-metric']}>
                  <div className={styles['sm-metric-label']}>Total Platform Spend</div>
                  <div className={styles['sm-metric-val']}>{account.currency} {totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className={styles['sm-metric-change'] + ' ' + styles['sm-metric-up']} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontSize: '10px' }}>⚡</span> Active Budget Tracking
                  </div>
                </div>

                <div className={styles['sm-metric']}>
                  <div className={styles['sm-metric-label']}>Synchronized Leads</div>
                  <div className={styles['sm-metric-val']}>{totalLeads.toLocaleString()}</div>
                  <div className={styles['sm-metric-change'] + ' ' + styles['sm-metric-up']} style={{ color: '#4a6508' }}>
                    Avg. CPL: <strong>{account.currency} {avgCpl.toFixed(2)}</strong>
                  </div>
                </div>

                <div className={styles['sm-metric']}>
                  <div className={styles['sm-metric-label']}>Campaign Clicks</div>
                  <div className={styles['sm-metric-val']}>{totalClicks.toLocaleString()}</div>
                  <div className={styles['sm-metric-change'] + ' ' + styles['sm-metric-up']} style={{ color: '#4a6508' }}>
                    Avg. CTR: <strong>{avgCtr.toFixed(2)}%</strong>
                  </div>
                </div>

                <div className={styles['sm-metric']}>
                  <div className={styles['sm-metric-label']}>Total Impressions</div>
                  <div className={styles['sm-metric-val']}>{totalImpressions.toLocaleString()}</div>
                  <div className={styles['sm-metric-change'] + ' ' + styles['sm-metric-up']} style={{ color: '#4a6508' }}>
                    Reach: <strong>{totalReach.toLocaleString()}</strong> | CPM: <strong>{avgCpm.toFixed(0)}</strong>
                  </div>
                </div>
              </div>

              <div className={styles['sm-two-col']} style={{ marginBottom: '24px' }}>
                {/* FUNNEL DIAGRAM */}
                <div className={styles['sm-bar-chart-wrap']} style={{ height: '100%', minHeight: '340px' }}>
                  <div className={styles['sm-chart-title']} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Lead Generation Funnel</span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 'normal' }}>Impressions to Conversion</span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '240px', padding: '10px 0' }}>
                    {totalImpressions === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center' }}>
                        No marketing impressions data to build funnel dashboard.
                      </div>
                    ) : (
                      <svg viewBox="0 0 400 220" width="100%" height="100%" style={{ overflow: 'visible' }}>
                        <polygon points="30,20 370,20 330,70 70,70" fill="rgba(121, 159, 12, 0.85)" filter="drop-shadow(0px 3px 4px rgba(0,0,0,0.08))" />
                        <text x="200" y="46" fill="#ffffff" textAnchor="middle" style={{ fontSize: '11px', fontWeight: 'bold', fontFamily: 'Plus Jakarta Sans' }}>
                          IMPRESSIONS: {totalImpressions.toLocaleString()}
                        </text>
                        
                        <polygon points="73,76 327,76 290,126 110,126" fill="rgba(121, 159, 12, 0.65)" filter="drop-shadow(0px 3px 4px rgba(0,0,0,0.06))" />
                        <text x="200" y="102" fill="#ffffff" textAnchor="middle" style={{ fontSize: '11px', fontWeight: 'bold', fontFamily: 'Plus Jakarta Sans' }}>
                          CLICKS: {totalClicks.toLocaleString()} (CTR: {avgCtr.toFixed(2)}%)
                        </text>
                        
                        <polygon points="113,132 287,132 250,182 150,182" fill="rgba(121, 159, 12, 0.45)" filter="drop-shadow(0px 3px 4px rgba(0,0,0,0.04))" />
                        <text x="200" y="158" fill="#ffffff" textAnchor="middle" style={{ fontSize: '11px', fontWeight: 'bold', fontFamily: 'Plus Jakarta Sans' }}>
                          LEADS: {totalLeads.toLocaleString()} (CPL: {account.currency} {avgCpl.toFixed(1)})
                        </text>

                        <rect x="90" y="196" width="220" height="24" rx="12" fill="#799F0C" />
                        <text x="200" y="212" fill="#ffffff" textAnchor="middle" style={{ fontSize: '10px', fontWeight: '800', fontFamily: 'Plus Jakarta Sans', letterSpacing: '0.04em' }}>
                          OVERALL CONVERSION: {overallConversion.toFixed(3)}%
                        </text>
                      </svg>
                    )}
                  </div>
                </div>

                {/* SPEND BY CAMPAIGN */}
                <div className={styles['sm-breakdown']} style={{ minHeight: '340px' }}>
                  <div className={styles['sm-chart-title']}>Top Campaigns by Spend</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
                    {topCampaigns.length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '40px 0' }}>
                        No campaign spend metrics available.
                      </div>
                    ) : (
                      topCampaigns.map((c) => {
                        const percentSpend = maxSpend > 0 ? (parseFloat(c.spend || 0) / maxSpend) * 100 : 0;
                        const cplVal = intVal(c.leads) > 0 ? parseFloat(c.spend || 0) / intVal(c.leads) : 0;
                        return (
                          <div key={c.id}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px', fontWeight: '600' }}>
                              <span style={{ color: '#111827', maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.campaign_name}
                              </span>
                              <span style={{ color: '#4b5563' }}>
                                {account.currency} {parseFloat(c.spend || 0).toLocaleString()} <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>({c.leads} leads)</span>
                              </span>
                            </div>
                            <div className={styles['sm-bd-bar-wrap']} style={{ height: '8px', background: '#f3f4f6', borderRadius: '4px' }}>
                              <div 
                                className={styles['sm-bd-bar']} 
                                style={{ 
                                  width: `${percentSpend}%`, 
                                  background: 'linear-gradient(90deg, #799F0C, #a8cc10)', 
                                  height: '100%', 
                                  borderRadius: '4px' 
                                }} 
                              />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: '#9ca3af', marginTop: '4px' }}>
                              <span>Status: <strong style={{ color: c.status === 'ACTIVE' ? '#2a7a38' : '#6b7280' }}>{c.status}</strong></span>
                              {cplVal > 0 && <span>CPL: <strong>{account.currency} {cplVal.toFixed(1)}</strong></span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className={styles['sm-two-col']}>
                {/* CAMPAIGN OBJECTIVE ANALYSIS */}
                <div className={styles['sm-breakdown']} style={{ padding: '20px' }}>
                  <div className={styles['sm-chart-title']}>Campaign Objective Spend Share</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
                    {objectivesList.length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>
                        No campaign objective data available.
                      </div>
                    ) : (
                      objectivesList.map((obj) => {
                        const pctObjSpend = maxObjectiveSpend > 0 ? (obj.spend / maxObjectiveSpend) * 100 : 0;
                        return (
                          <div key={obj.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '35%', fontSize: '11px', fontWeight: 'bold', color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {obj.name}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div className={styles['sm-bd-bar-wrap']} style={{ height: '6px', background: '#f3f4f6', borderRadius: '3px' }}>
                                <div 
                                  className={styles['sm-bd-bar']} 
                                  style={{ 
                                    width: `${pctObjSpend}%`, 
                                    background: '#799F0C', 
                                    height: '100%', 
                                    borderRadius: '3px' 
                                  }} 
                                />
                              </div>
                            </div>
                            <div style={{ width: '25%', textAnchor: 'end', fontSize: '11.5px', fontWeight: '700', color: '#111827', textAlign: 'right' }}>
                              {account.currency} {obj.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* AD ACCOUNT DATA REPORT SUMMARY */}
                <div className={styles['sm-bar-chart-wrap']} style={{ padding: '20px', display: 'flex', flexDirection: 'column', justifycontent: 'space-between' }}>
                  <div className={styles['sm-chart-title']}>Meta Ads Report Synopsis</div>
                  <div style={{ fontSize: '12.5px', color: '#6b7280', lineHeight: '1.6' }}>
                    <p style={{ marginBottom: '10px' }}>
                      This dashboard displays cached marketing metrics fetched directly from Facebook Ads. Currently tracking <strong>{campaigns.length} campaigns</strong> under this ad account.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', background: '#fafafa', padding: '12px', borderRadius: '8px', border: '1px solid #f0f0f0', marginTop: '10px' }}>
                      <div>
                        <div style={{ fontSize: '10.5px', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase' }}>Avg Clicks/Campaign</div>
                        <div style={{ fontSize: '14px', fontWeight: '800', color: '#374151', marginTop: '2px' }}>
                          {campaigns.length > 0 ? (totalClicks / campaigns.length).toFixed(0) : 0}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '10.5px', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase' }}>Avg Leads/Campaign</div>
                        <div style={{ fontSize: '14px', fontWeight: '800', color: '#374151', marginTop: '2px' }}>
                          {campaigns.length > 0 ? (totalLeads / campaigns.length).toFixed(0) : 0}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#36a849' }} />
                    Database updated automatically via Meta Marketing API integrations.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CAMPAIGNS TAB */}
          {activeTab === 'campaigns' && (
            <div className={styles['sm-table-card']}>
              <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Active Meta Campaigns List</h3>
                <span style={{ fontSize: '11.5px', color: '#9ca3af' }}>Showing {campaigns.length} campaigns</span>
              </div>
              
              <div className={styles['sm-table-wrap']}>
                <table className={styles['sm-table']}>
                  <thead>
                    <tr>
                      <th>Campaign Name</th>
                      <th>Objective</th>
                      <th>Status</th>
                      <th>Spend</th>
                      <th>Impressions</th>
                      <th>Clicks</th>
                      <th>Leads</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '40px 16px', color: '#9ca3af' }}>
                          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📣</div>
                          <strong>No campaigns cached for this account</strong>
                          <div style={{ fontSize: '11.5px', marginTop: '4px' }}>Ensure active campaigns are running inside Meta Ads Manager.</div>
                        </td>
                      </tr>
                    ) : (
                      campaigns.map(c => {
                        const isAct = c.status === 'ACTIVE' || c.status === 'active';
                        const cCfg = getStatusCfg(c.status);
                        return (
                          <tr key={c.id}>
                            <td style={{ fontWeight: '700', color: '#111827' }}>{c.campaign_name}</td>
                            <td>
                              <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                                {(c.objective || 'OTHER').replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td>
                              <span className={styles['sm-status']} style={{ background: cCfg.bg, color: cCfg.color }}>
                                <div className={styles['sm-status-dot']} style={{ background: cCfg.dot }} />
                                {cCfg.label}
                              </span>
                            </td>
                            <td>{account.currency} {parseFloat(c.spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                            <td>{intVal(c.impressions).toLocaleString()}</td>
                            <td>{intVal(c.clicks).toLocaleString()}</td>
                            <td style={{ fontWeight: 'bold' }}>{intVal(c.leads)}</td>
                            <td>
                              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => handleToggleCampaign(c.campaign_id, c.status)}
                                  className={styles['sm-io-btn']}
                                  style={{ padding: '5px 10px', fontSize: '11.5px', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  <i className={isAct ? "fa-solid fa-pause" : "fa-solid fa-play"} />
                                  {isAct ? 'Pause' : 'Activate'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MAPPINGS TAB */}
          {activeTab === 'mappings' && (
            <div className={styles['sm-two-col']}>
              <div className={styles['sm-cal-card']} style={{ height: 'fit-content' }}>
                <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '12px', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Create Form Mapping Rule</h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11.5px', color: '#9ca3af' }}>Route leads generated from Facebook Leadgen forms directly into CRM pipelines.</p>
                </div>
                
                <form onSubmit={handleCreateMapping} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div className={styles['sm-form-field']}>
                    <label className={styles['sm-form-label']}>Select Facebook Lead Form</label>
                    <select
                      className={styles['sm-form-input']}
                      value={selectedForm}
                      onChange={e => setSelectedForm(e.target.value)}
                    >
                      <option value="">-- Choose Lead Form --</option>
                      {leadForms.map(f => (
                        <option key={f.id} value={f.id}>{f.name} ({f.leads_count || 0} leads)</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles['sm-form-field']}>
                    <label className={styles['sm-form-label']}>Meta Campaign Filter (Optional)</label>
                    <input
                      type="text"
                      className={styles['sm-form-input']}
                      placeholder="e.g. Summer Campaign"
                      value={campaignName}
                      onChange={e => setCampaignName(e.target.value)}
                    />
                  </div>

                  <div className={styles['sm-form-field']}>
                    <label className={styles['sm-form-label']}>Target Lead Pipeline Status</label>
                    <select
                      className={styles['sm-form-input']}
                      value={selectedStatus}
                      onChange={e => setSelectedStatus(e.target.value)}
                    >
                      <option value="">-- Choose Pipeline Status --</option>
                      {PIPELINE_STATUSES.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <button type="submit" className={styles['sm-add-btn']} style={{ width: '100%', justifyContent: 'center', marginTop: '6px' }}>
                    <i className="fa-solid fa-plus" /> Save Mapping Rule
                  </button>
                </form>
              </div>

              <div className={styles['sm-table-card']} style={{ height: 'fit-content' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Configured Mappings</h3>
                </div>
                
                <div className={styles['sm-table-wrap']}>
                  <table className={styles['sm-table']}>
                    <thead>
                      <tr>
                        <th>Form ID / Name</th>
                        <th>Campaign</th>
                        <th>Pipeline Status</th>
                        <th>Sync Count</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '36px 12px', color: '#6b7280' }}>
                            No active mapping rules configured yet. Incoming webhook leads will fallback to default status.
                          </td>
                        </tr>
                      ) : (
                        mappings.map(m => (
                          <tr key={m.id}>
                            <td>
                              <div style={{ fontWeight: '700', color: '#111827' }}>{m.form_name || 'Unnamed Form'}</div>
                              <span style={{ fontSize: '10.5px', color: '#9ca3af' }}>ID: {m.form_id}</span>
                            </td>
                            <td>{m.campaign_name || <span style={{ color: '#9ca3af', fontSize: '12px' }}>All campaigns</span>}</td>
                            <td>
                              <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '700' }}>
                                {m.default_status || 'New'}
                              </span>
                            </td>
                            <td style={{ fontWeight: '700' }}>{m.lead_count}</td>
                            <td>
                              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button 
                                  onClick={() => handleDeleteMapping(m.id)}
                                  className={`${styles['sm-action-btn']} ${styles['danger']}`}
                                  title="Delete Mapping Rule"
                                  style={{ border: 'none', background: 'transparent' }}
                                >
                                  <i className="fa-solid fa-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* STAGING LEADS BUFFER TAB */}
          {activeTab === 'buffer' && (
            <div className={styles['sm-table-card']}>
              <div style={{ padding: '18px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Staging Leads Buffer</h3>
                <span style={{ fontSize: '11.5px', color: '#9ca3af' }}>
                  Incoming Meta Leads (Click Transfer to import into active CRM leads list)
                </span>
              </div>
              
              <div className={styles['sm-table-wrap']}>
                <table className={styles['sm-table']}>
                  <thead>
                    <tr>
                      <th>Prospect Contact Details</th>
                      <th>Campaign & Form ID</th>
                      <th>Requested Course</th>
                      <th>Status</th>
                      <th>Received Date</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bufferLoading ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px 16px', color: '#9ca3af' }}>
                          ♻️ Loading staging leads...
                        </td>
                      </tr>
                    ) : bufferLeads.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '52px 16px', color: '#9ca3af' }}>
                          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📥</div>
                          <strong>No leads stored in the staging buffer yet</strong>
                          <div style={{ fontSize: '11.5px', marginTop: '4px' }}>Ensure Facebook webhook integration is configured and active.</div>
                        </td>
                      </tr>
                    ) : (
                      bufferLeads.map(lead => {
                        const isPending = lead.status === 'pending';
                        const isTransferred = lead.status === 'transferred';
                        const isIgnored = lead.status === 'ignored';

                        let statusBg = '#f3f4f6';
                        let statusColor = '#4b5563';
                        let statusLabel = 'Unknown';
                        if (isPending) {
                          statusBg = '#fef3c7';
                          statusColor = '#d97706';
                          statusLabel = 'Pending Review';
                        } else if (isTransferred) {
                          statusBg = '#d1fae5';
                          statusColor = '#065f46';
                          statusLabel = 'Transferred';
                        } else if (isIgnored) {
                          statusBg = '#fee2e2';
                          statusColor = '#991b1b';
                          statusLabel = 'Ignored';
                        }

                        return (
                          <tr key={lead.id}>
                            <td>
                              <div style={{ fontWeight: '700', color: '#111827' }}>
                                {lead.first_name} {lead.last_name}
                              </div>
                              <div style={{ fontSize: '11.5px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                {lead.email && <span>📧 {lead.email}</span>}
                                {lead.phone && <span>📞 {lead.phone}</span>}
                              </div>
                            </td>
                            <td>
                              <div style={{ fontWeight: '600', color: '#4b5563' }}>{lead.form_name || 'Facebook Lead Form'}</div>
                              <span style={{ fontSize: '10.5px', color: '#9ca3af' }}>
                                Campaign: {lead.campaign_name || 'N/A'} (Form ID: {lead.form_id})
                              </span>
                            </td>
                            <td>
                              <span style={{ fontSize: '11.5px', color: '#374151', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                                {lead.course || 'No Course Selected'}
                              </span>
                            </td>
                            <td>
                              <span style={{ display: 'inline-block', background: statusBg, color: statusColor, padding: '3px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '700' }}>
                                {statusLabel}
                              </span>
                            </td>
                            <td style={{ fontSize: '12px', color: '#6b7280' }}>
                              {new Date(lead.created_at).toLocaleString()}
                            </td>
                            <td>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                {isPending ? (
                                  <>
                                    <button
                                      onClick={() => handleTransferLead(lead, false, 'create')}
                                      className={styles['sm-add-btn']}
                                      style={{ padding: '6px 12px', fontSize: '11.5px', background: 'linear-gradient(135deg,#799F0C,#a8cc10)', color: '#fff' }}
                                    >
                                      <i className="fa-solid fa-file-import" /> Transfer
                                    </button>
                                    <button
                                      onClick={() => handleIgnoreLead(lead.id)}
                                      className={`${styles['sm-action-btn']} ${styles['danger']}`}
                                      style={{ border: '1.5px solid #fca5a5' }}
                                      title="Ignore Prospect"
                                    >
                                      <i className="fa-solid fa-ban" />
                                    </button>
                                  </>
                                ) : isTransferred ? (
                                  <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <i className="fa-solid fa-circle-check" /> Linked to CRM
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '11.5px', color: '#9ca3af', fontWeight: '500' }}>
                                    Archived
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        /* DISCONNECTED STATE */
        <div className={styles['sm-table-card']} style={{ textAlign: 'center', padding: '64px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
            <i className="fa-brands fa-facebook" style={{ fontSize: '38px', color: '#1877F2' }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: '0 0 10px 0' }}>No Meta Ads Account Connected</h2>
          <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto 28px auto', fontSize: '13.5px', lineHeight: '1.6' }}>
            Authorize and link your Facebook Ad Account to pull active marketing campaigns data, track cost-per-lead insights, and automatically route Facebook Form submissions directly into your CRM.
          </p>
          <button 
            className={styles['sm-add-btn']} 
            onClick={handleConnect}
            style={{ 
              padding: '12px 24px', 
              fontSize: '14px', 
              background: 'linear-gradient(135deg, #1877F2, #115cbf)', 
              boxShadow: '0 4px 12px rgba(24, 119, 242, 0.3)' 
            }}
          >
            <i className="fa-solid fa-plug" style={{ marginRight: '6px' }} /> Connect Meta Ads Account
          </button>
        </div>
      )}

      {/* DUPLICATE WARNING MODAL */}
      {transferModalLead && (
        <div className={styles['sm-modal-backdrop']}>
          <div className={styles['sm-modal']} style={{ maxWidth: '480px' }}>
            <div className={styles['sm-modal-head']}>
              <div>
                <div className={styles['sm-modal-eyebrow']}>Duplicate Detected</div>
                <h3 className={styles['sm-modal-title']} style={{ margin: 0 }}>Resolve Staging Lead Import</h3>
              </div>
              <button className={styles['sm-modal-close']} onClick={() => setTransferModalLead(null)}>×</button>
            </div>
            
            <div className={styles['sm-modal-body']} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ background: '#fffbeb', border: '1.5px solid #fef3c7', padding: '12px 16px', borderRadius: '10px', color: '#b45309', fontSize: '12.5px', lineHeight: '1.6' }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px' }} />
                {duplicateWarning}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', gap: '10px', cursor: 'pointer', background: '#fafafa', padding: '12px', borderRadius: '8px', border: '1px solid #eee' }}>
                  <input
                    type="radio"
                    name="transferMode"
                    value="merge"
                    checked={transferMode === 'merge'}
                    onChange={() => setTransferMode('merge')}
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>Merge & Log Follow-up (Recommended)</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                      Appends the details as an interaction followup log under the existing lead without creating duplicates.
                    </div>
                  </div>
                </label>

                <label style={{ display: 'flex', gap: '10px', cursor: 'pointer', background: '#fafafa', padding: '12px', borderRadius: '8px', border: '1px solid #eee' }}>
                  <input
                    type="radio"
                    name="transferMode"
                    value="create"
                    checked={transferMode === 'create'}
                    onChange={() => setTransferMode('create')}
                    style={{ marginTop: '3px' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>Create New Lead Profile anyway</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                      Forces importing as a separate lead record, creating multiple contacts with the same info.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className={styles['sm-modal-foot']}>
              <button className={styles['sm-btn-cancel']} onClick={() => setTransferModalLead(null)}>
                Cancel
              </button>
              <button
                className={styles['sm-btn-save']}
                onClick={() => handleTransferLead(transferModalLead, true, transferMode)}
                style={{ background: 'linear-gradient(135deg, #799F0C, #a8cc10)', color: '#fff' }}
              >
                Confirm Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function intVal(val) {
  const parsed = parseInt(val, 10);
  return isNaN(parsed) ? 0 : parsed;
}
